import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Star, Heart, MessageSquare, Clock, TrendingUp, Copy, ExternalLink, QrCode, Printer } from "lucide-react";

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={16} className={i <= Math.round(value) ? "text-amber-500 fill-amber-500" : "text-slate-300"} />
    ))}
  </span>
);

const MONTH_LABELS = ["jan.", "fév.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const monthLabel = (key) => {
  const m = parseInt((key || "").slice(5, 7), 10);
  return m >= 1 && m <= 12 ? MONTH_LABELS[m - 1] : key;
};

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Icon size={16} className="text-[#E05638]" /> {label}
      </div>
      <div className="mt-1.5 font-heading text-2xl font-extrabold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ProReputation() {
  const [data, setData] = useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/pro/reputation").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast.success(msg || "Lien copié !"); }
    catch { toast.message("Copie manuelle requise."); }
  };

  if (data === null) return <div className="text-slate-500">Chargement…</div>;
  if (data === false) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center" data-testid="pro-reputation-nofiche">
        <TrendingUp size={36} className="mx-auto text-slate-300" />
        <div className="mt-2 font-heading font-extrabold text-slate-900">Créez d'abord votre mini-site</div>
        <p className="mt-1 text-sm text-slate-600">Votre réputation s'affichera ici dès que votre fiche sera active.</p>
        <Link to="/pro/entreprise"><Button className="mt-4 btn-shine text-white" data-testid="pro-reputation-create-fiche">Compléter ma fiche</Button></Link>
      </div>
    );
  }

  const s = data.summary || { average: 0, count: 0, distribution: {}, criteria_averages: {} };
  const dist = s.distribution || {};
  const crit = s.criteria_averages || {};
  const maxEvo = Math.max(1, ...(data.evolution || []).map((e) => e.count));
  const bizId = data.business?.id;
  const reviewLink = data.review_link || "";
  const googleUrl = (data.google_review_url || "").trim();

  const CRIT_LABELS = {
    accueil: "Accueil", qualite_prix: "Qualité / prix", reactivite: "Réactivité",
    qualite: "Qualité", proprete: "Propreté", ambiance: "Ambiance",
  };

  return (
    <div data-testid="pro-reputation-page">
      <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Ma réputation</h1>
      <p className="mt-1 text-sm text-slate-500">
        Suivez votre note, vos recommandations et l'évolution de vos avis, puis collectez-en de nouveaux.
      </p>

      {/* Cartes synthèse */}
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pro-reputation-stats">
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="pro-reputation-note">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Star size={16} className="text-amber-500" /> Note moyenne</div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="font-heading text-2xl font-extrabold text-slate-900">{s.average || "—"}</span>
            <Stars value={s.average} />
          </div>
          <div className="text-xs text-slate-400 mt-0.5">{s.count} avis publié{s.count > 1 ? "s" : ""}</div>
        </div>
        <StatCard icon={Heart} label="Recommandations" value={data.reco_count} sub="« Je recommande »" testId="pro-reputation-reco" />
        <StatCard icon={MessageSquare} label="Taux de réponse" value={`${data.reply_rate}%`} sub="aux avis publiés" testId="pro-reputation-replyrate" />
        <StatCard icon={Clock} label="En attente" value={data.pending} sub="en modération" testId="pro-reputation-pending" />
      </div>

      {/* Répartition + Évolution */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="pro-reputation-distribution">
          <div className="font-heading font-extrabold text-slate-900">Répartition des notes</div>
          <div className="mt-4 space-y-2">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = dist[String(n)] || 0;
              const pct = s.count ? Math.round((100 * c) / s.count) : 0;
              return (
                <div key={n} className="flex items-center gap-2 text-sm">
                  <span className="w-10 inline-flex items-center gap-0.5 text-slate-600">{n} <Star size={12} className="text-amber-500 fill-amber-500" /></span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-right text-slate-500 text-xs">{c}</span>
                </div>
              );
            })}
          </div>
          {Object.keys(crit).length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Notes par critère</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(crit).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                    {CRIT_LABELS[k] || k} <span className="text-amber-600">{v}/5</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="pro-reputation-evolution">
          <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2">
            <TrendingUp size={18} className="text-[#E05638]" /> Évolution (6 mois)
          </div>
          {s.count === 0 ? (
            <div className="mt-6 flex flex-col items-center justify-center h-36 text-center" data-testid="pro-reputation-evolution-empty">
              <MessageSquare size={28} className="text-slate-300" />
              <p className="mt-2 text-sm font-semibold text-slate-500">Pas encore d'avis</p>
              <p className="text-xs text-slate-400">Collectez vos premiers avis grâce au QR code ci-dessous.</p>
            </div>
          ) : (
            <div className="mt-5 flex items-end justify-between gap-2 h-36">
              {(data.evolution || []).map((e) => (
                <div key={e.month} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`evo-${e.month}`}>
                  <span className="text-[11px] font-bold text-slate-500">{e.count || ""}</span>
                  <div className="w-full flex items-end justify-center" style={{ height: "100px" }}>
                    <div
                      className="w-7 rounded-t-md bg-[#E05638]/80 transition-all"
                      style={{ height: `${Math.max(4, (e.count / maxEvo) * 100)}%` }}
                      title={`${e.count} avis · moyenne ${e.average || "—"}`}
                    />
                  </div>
                  <span className="text-[11px] text-slate-400">{monthLabel(e.month)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-slate-400">Nombre d'avis publiés par mois.</p>
        </div>
      </div>

      {/* Collecte d'avis */}
      <div className="mt-6 rounded-2xl border border-[#E05638]/30 bg-[#FFF4EC]/60 p-5" data-testid="pro-reputation-collect">
        <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
          <QrCode size={18} className="text-[#E05638]" /> Collecter des avis
        </div>
        <p className="mt-1 text-sm text-slate-600">
          Partagez votre QR code ou votre lien direct : vos clients laissent un avis sur votre fiche PROXIreduc en quelques secondes.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-4 items-start">
          {bizId && (
            <img
              src={`${BACKEND}/api/businesses/${bizId}/proxi-review-qr.png`}
              alt="QR avis PROXIreduc"
              className="w-32 h-32 rounded-xl border border-slate-200 bg-white p-2 shrink-0"
              data-testid="pro-reputation-qr"
            />
          )}
          <div className="flex flex-col gap-2 w-full min-w-0 flex-1">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 min-w-0">
              <span className="text-sm text-slate-600 truncate flex-1 min-w-0" data-testid="pro-reputation-link">{reviewLink}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => copy(reviewLink)} data-testid="pro-reputation-copy-link">
                <Copy size={14} className="mr-1.5" /> Copier le lien
              </Button>
              <a href={reviewLink} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="pro-reputation-open-link"><ExternalLink size={14} className="mr-1.5" /> Voir ma fiche</Button>
              </a>
              <Link to="/pro/avis">
                <Button className="btn-shine text-white" data-testid="pro-reputation-reply"><MessageSquare size={14} className="mr-1.5" /> Répondre aux avis</Button>
              </Link>
            </div>

            {googleUrl && (
              <div className="mt-2 flex flex-wrap items-center gap-2 pt-2 border-t border-[#E05638]/15">
                <span className="text-xs font-semibold text-slate-500">Avis Google :</span>
                <a href={googleUrl} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" data-testid="pro-reputation-google-open"><ExternalLink size={13} className="mr-1.5" /> Ouvrir</Button>
                </a>
                <Button variant="outline" size="sm" onClick={() => copy(googleUrl, "Lien Google copié !")} data-testid="pro-reputation-google-copy">
                  <Copy size={13} className="mr-1.5" /> Copier
                </Button>
                {bizId && (
                  <a href={`${BACKEND}/api/businesses/${bizId}/google-review-poster`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" data-testid="pro-reputation-google-poster"><Printer size={13} className="mr-1.5" /> Présentoir</Button>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
